import { toast } from "sonner";
import { desktop } from "@/desktop/bridge";
import { useBreakpoints } from "@/workspace/breakpoints";
import { activeEditor } from "@/workspace/active-editor";
import { activeWorkspace } from "@/workspace/file-ops";
import { useWorkspace } from "@/workspace/store";
import { runInTerminal, runProject } from "@/workspace/terminal-panel";
import { fileDebugCommand, fileRunCommand, runOfferFor, type RunOffer } from "./file-run";
import { runsAvailable, startLive } from "./local-runs";
import { useRunsUi } from "./runs-ui";

/** Arquivo em foco no projeto aberto (id e caminho), ou null. */
export function activeFile(): { id: string; path: string } | null {
  const ws = activeWorkspace.current;
  const id = useWorkspace.getState().active;
  if (!ws || !id) return null;
  const path = ws.session.tree().paths.get(id);
  return path ? { id, path } : null;
}

/** O que o Executar oferece para o arquivo em foco. */
export function activeOffer(): RunOffer {
  const ws = activeWorkspace.current;
  const file = activeFile();
  if (!ws || !file) return { kind: "none" };
  return runOfferFor(file.path, ws.session.tree().paths.values());
}

const platform = () => desktop?.platform ?? "linux";

/** Executar o arquivo aberto (Ctrl+F5): pela linguagem; HTML no Live Server; JS/TS de Node, o projeto. */
export async function runActiveFile(options: { dedicated?: boolean } = {}) {
  const ws = activeWorkspace.current;
  const file = activeFile();
  if (!ws || !file) return;
  const offer = runOfferFor(file.path, ws.session.tree().paths.values());
  if (offer.kind === "live") {
    if (runsAvailable) await startLive({ id: ws.detail.project.id, name: ws.detail.project.name });
    else await runProject(ws.session);
    return;
  }
  if (offer.kind === "project") return runProject(ws.session);
  if (offer.kind === "none") {
    toast("Este arquivo não se executa sozinho", {
      description: "Executar vale para Python, Java, C/C++, Go, Rust… HTML abre em Pré-visualizar.",
    });
    return;
  }
  await runInTerminal(ws.session, (target) => fileRunCommand(file.path, target, platform()), {
    dedicated: options.dedicated,
    label: `Executar ${offer.runner.label}`,
  });
}

/**
 * Depurar o arquivo aberto (Alt+F5) com os pontos de parada dele: Python no pdb e C/C++ no gdb,
 * num terminal só para isso.
 */
export async function debugActiveFile() {
  const ws = activeWorkspace.current;
  const file = activeFile();
  if (!ws || !file) return;
  const offer = runOfferFor(file.path, ws.session.tree().paths.values());
  if (offer.kind !== "file" || !offer.runner.debuggable) {
    toast("Depuração com pontos de parada: Python e C/C++", {
      description:
        offer.kind === "file"
          ? `Para ${offer.runner.label}, use Executar e mensagens no terminal por enquanto.`
          : "Abra um arquivo .py, .c ou .cpp.",
    });
    return;
  }
  const lines = activeEditor.current?.breakpoints() ?? useBreakpoints.getState().files[file.id] ?? [];
  const python = offer.runner.language === "python";
  toast(
    lines.length ? `Depurando com ${lines.length} ponto(s) de parada` : "Depurando desde a primeira linha",
    {
      description: python
        ? "No terminal: n próxima linha · s entrar · p variável mostra · c continuar · q sair."
        : "No terminal (gdb): next · step · print variável · continue · quit.",
      duration: 9000,
    },
  );
  await runInTerminal(ws.session, (target) => fileDebugCommand(file.path, lines, target, platform()), {
    dedicated: true,
    label: `Depurar ${offer.runner.label}`,
  });
}

/** "Execuções desta máquina": abre depois que o menu de onde veio terminou de fechar. */
export function showMachineRuns() {
  setTimeout(() => useRunsUi.setState({ menu: true }), 60);
}
